<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper ">
    <div class="content">
      <div class="page-header">
        <div class="add-item d-flex">
          <div class="page-title">
            <h4>Paiements des Factures</h4>
            <h6>Suivre les règlements et le reste à payer</h6>
          </div>
        </div>
        <div class="page-btn">
          <button v-can="'payments.create'" type="button" class="btn btn-added" @click="openCreateModal">
            <vue-feather type="plus-circle" class="me-2"></vue-feather>
            Ajouter un paiement
          </button>
        </div>
      </div>

      <div v-if="activeFacture" class="card border-info shadow-sm mb-3">
        <div class="card-body">
          <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <div class="fw-semibold">{{ activeFacture.numero_facture }}</div>
              <div class="text-muted small">{{ activeFacture.client_name || "Client" }}</div>
            </div>
            <div class="summary-chip">
              <span class="summary-label">Total</span>
              <strong>{{ formatCurrency(activeFacture.montant_total) }}</strong>
            </div>
            <div class="summary-chip">
              <span class="summary-label">Déjà payé</span>
              <strong>{{ formatCurrency(activeFacture.montant_paye) }}</strong>
            </div>
            <div class="summary-chip">
              <span class="summary-label">Reste</span>
              <strong>{{ formatCurrency(activeFacture.montant_restant) }}</strong>
            </div>
          </div>
        </div>
      </div>

      <div class="card table-list-card">
        <div class="card-body">
          <div class="table-top d-flex align-items-center justify-content-between flex-wrap row">
            <div class="col-lg-5 col-md-6">
              <label class="form-label">Recherche</label>
              <input
                v-model="searchQuery"
                type="text"
                class="form-control"
                placeholder="Facture, client, référence, utilisateur..."
                @input="applyFilters"
              />
            </div>
            <div class="col-lg-5 col-md-6">
              <label class="form-label">Filtrer par facture</label>
              <select v-model="filterFactureId" class="form-select" @change="onFactureFilterChange">
                <option value="">Toutes les factures</option>
                <option v-for="facture in factures" :key="facture.id" :value="String(facture.id)">
                  {{ factureLabel(facture) }}
                </option>
              </select>
            </div>
            <div class="col-lg-2 col-md-12">
              <button type="button" class="btn btn-outline-secondary w-100" @click="clearFilters">
                Réinitialiser
              </button>
            </div>
          </div>

          <div v-if="isLoading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover align-middle">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Facture</th>
                  <th>Client</th>
                  <th>Montant payé</th>
                  <th>Reste avant</th>
                  <th>Reste après</th>
                  <th>Mode</th>
                  <th>Utilisateur</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="payment in filteredPayments" :key="payment.id">
                  <td>{{ formatDate(payment.date_paiement) }}</td>
                  <td>
                    <div class="fw-semibold">{{ payment.facture_reference }}</div>
                    <div v-if="payment.reference" class="text-muted small">{{ payment.reference }}</div>
                  </td>
                  <td>{{ payment.client_name || "—" }}</td>
                  <td class="fw-semibold text-success">{{ formatCurrency(payment.montant) }}</td>
                  <td>{{ formatCurrency(payment.montant_restant_avant) }}</td>
                  <td>{{ formatCurrency(payment.montant_restant_apres) }}</td>
                  <td>{{ payment.mode_paiement || "—" }}</td>
                  <td>{{ payment.user_name || "—" }}</td>
                  <td>
                    <div class="d-flex gap-2">
                      <button v-can="'payments.update'" type="button" class="btn btn-sm btn-outline-primary" @click="openEditModal(payment)">
                        Modifier
                      </button>
                      <button v-can="'payments.delete'" type="button" class="btn btn-sm btn-outline-danger" @click="confirmDelete(payment)">
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!filteredPayments.length">
                  <td colspan="9" class="text-center text-muted py-4">
                    Aucun paiement enregistré.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="modal fade" ref="paymentModalEl" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <form @submit.prevent="submitPayment">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingPayment ? "Modifier le paiement" : "Ajouter un paiement" }}
            </h5>
            <button type="button" class="btn-close" aria-label="Close" @click="hideModal"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-md-12">
                <label class="form-label">Facture</label>
                <select v-model="form.facture_id" class="form-select" required>
                  <option value="" disabled>Choisir une facture</option>
                  <option v-for="facture in factures" :key="facture.id" :value="String(facture.id)">
                    {{ factureLabel(facture) }}
                  </option>
                </select>
              </div>

              <div v-if="selectedFormFacture" class="col-12">
                <div class="border rounded p-3 bg-light">
                  <div class="row g-3">
                    <div class="col-md-4">
                      <div class="small text-muted">Total facture</div>
                      <div class="fw-semibold">{{ formatCurrency(selectedFormFacture.montant_total) }}</div>
                    </div>
                    <div class="col-md-4">
                      <div class="small text-muted">Déjà payé</div>
                      <div class="fw-semibold">{{ formatCurrency(selectedFormFacture.montant_paye) }}</div>
                    </div>
                    <div class="col-md-4">
                      <div class="small text-muted">Reste actuel</div>
                      <div class="fw-semibold">{{ formatCurrency(selectedFormFacture.montant_restant) }}</div>
                    </div>
                  </div>
                  <div class="row g-3 mt-1">
                    <div class="col-md-6">
                      <div class="small text-muted">Déjà payé après cette opération</div>
                      <div class="fw-semibold text-success">
                        {{ formatCurrency(projectedPaidAmount) }}
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="small text-muted">Reste après cette opération</div>
                      <div class="fw-semibold" :class="{ 'text-danger': projectedRemainingAmount > 0 }">
                        {{ formatCurrency(projectedRemainingAmount) }}
                      </div>
                    </div>
                  </div>
                  <div class="small text-muted mt-2">
                    Montant maximum autorisé pour cette opération :
                    <strong>{{ formatCurrency(maxAllowedAmount) }}</strong>
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <label class="form-label">Montant payé</label>
                <input
                  v-model.number="form.montant"
                  type="number"
                  min="0.01"
                  step="0.01"
                  class="form-control"
                  :max="maxAllowedAmount || null"
                  required
                />
                <div v-if="isAmountOverLimit" class="invalid-feedback d-block">
                  Le montant dépasse le reste autorisé pour la facture.
                </div>
              </div>

              <div class="col-md-4">
                <label class="form-label">Mode de paiement</label>
                <select v-model="form.mode_paiement" class="form-select">
                  <option v-for="mode in paymentModes" :key="mode" :value="mode">
                    {{ mode }}
                  </option>
                </select>
              </div>

              <div class="col-md-4">
                <label class="form-label">Date de paiement</label>
                <input v-model="form.date_paiement" type="date" class="form-control" />
              </div>

              <div class="col-md-6">
                <label class="form-label">Référence</label>
                <input
                  v-model.trim="form.reference"
                  type="text"
                  class="form-control"
                  maxlength="150"
                  placeholder="Référence optionnelle"
                />
              </div>

              <div class="col-md-6">
                <label class="form-label">Notes</label>
                <textarea
                  v-model.trim="form.notes"
                  class="form-control"
                  rows="3"
                  maxlength="1000"
                  placeholder="Notes internes"
                ></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="hideModal">
              Annuler
            </button>
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting || isAmountOverLimit">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { toast } from "vue3-toastify";
import { getFacturesData } from "@/services/facturesService";
import {
  getFacturePaymentsData,
  createFacturePayment,
  updateFacturePayment,
  deleteFacturePayment,
} from "@/services/facturePaymentsService";

const defaultPaymentMode = "Espèces";

export default {
  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      payments: [],
      filteredPayments: [],
      factures: [],
      searchQuery: "",
      filterFactureId: "",
      editingPayment: null,
      paymentModes: ["Espèces", "Virement", "Chèque", "Carte", "Traite", "Autre"],
      form: {
        facture_id: "",
        montant: null,
        mode_paiement: defaultPaymentMode,
        date_paiement: new Date().toISOString().slice(0, 10),
        reference: "",
        notes: "",
      },
      paymentModal: null,
    };
  },
  computed: {
    activeFacture() {
      return this.factures.find((item) => String(item.id) === String(this.filterFactureId)) || null;
    },
    selectedFormFacture() {
      return this.factures.find((item) => String(item.id) === String(this.form.facture_id)) || null;
    },
    maxAllowedAmount() {
      const facture = this.selectedFormFacture;
      if (!facture) return 0;

      if (
        this.editingPayment &&
        String(this.editingPayment.facture_id) === String(facture.id)
      ) {
        return this.roundMoney(facture.montant_restant + Number(this.editingPayment.montant || 0));
      }

      return this.roundMoney(facture.montant_restant);
    },
    projectedPaidAmount() {
      const facture = this.selectedFormFacture;
      if (!facture) return 0;

      let basePaid = Number(facture.montant_paye || 0);
      if (
        this.editingPayment &&
        String(this.editingPayment.facture_id) === String(facture.id)
      ) {
        basePaid -= Number(this.editingPayment.montant || 0);
      }

      return Math.max(this.roundMoney(basePaid + Number(this.form.montant || 0)), 0);
    },
    projectedRemainingAmount() {
      const facture = this.selectedFormFacture;
      if (!facture) return 0;

      return Math.max(
        this.roundMoney(Number(facture.montant_total || 0) - this.projectedPaidAmount),
        0
      );
    },
    isAmountOverLimit() {
      if (!this.form.montant || !this.maxAllowedAmount) {
        return false;
      }

      return Number(this.form.montant) > Number(this.maxAllowedAmount);
    },
  },
  watch: {
    "$route.query.facture_id"() {
      this.applyRouteFilter();
    },
  },
  methods: {
    normalize(value) {
      return String(value ?? "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
    roundMoney(value) {
      return Math.round((Number(value) || 0) * 100) / 100;
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("fr-MA", {
        style: "currency",
        currency: "MAD",
        minimumFractionDigits: 2,
      }).format(Number(value) || 0);
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);

      return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(date);
    },
    toDateInput(value) {
      if (!value) return new Date().toISOString().slice(0, 10);
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
      return date.toISOString().slice(0, 10);
    },
    emptyToNull(value) {
      return value === "" ? null : value;
    },
    factureLabel(facture) {
      return `${facture.numero_facture} - ${facture.client_name || "Client"}`;
    },
    mapFacture(item) {
      const client = item.commande?.client || null;
      const clientName =
        item.client_name ||
        client?.nom_societe ||
        [client?.prenom, client?.nom].filter(Boolean).join(" ").trim() ||
        "Client";

      return {
        id: item.id,
        numero_facture: item.numero_facture || `FCT-${item.id}`,
        client_name: clientName,
        montant_total: Number(item.montant_total || 0),
        montant_paye: Number(item.montant_payé || 0),
        montant_restant: Number(item.montant_restant || 0),
        statut_paiement: item.statut_paiement || "",
      };
    },
    mapPayment(item) {
      const facture = item.facture || {};
      const client = facture.commande?.client || null;

      return {
        id: item.id,
        facture_id: item.facture_id || facture.id,
        facture_reference: facture.numero_facture || `FCT-${item.facture_id}`,
        client_name:
          facture.client_name ||
          client?.nom_societe ||
          [client?.prenom, client?.nom].filter(Boolean).join(" ").trim() ||
          "Client",
        montant: Number(item.montant || 0),
        montant_restant_avant: Number(item.montant_restant_avant || 0),
        montant_restant_apres: Number(item.montant_restant_apres || 0),
        mode_paiement: item.mode_paiement || "—",
        reference: item.reference || "",
        notes: item.notes || "",
        date_paiement: item.date_paiement,
        user_name: item.user?.name || "—",
      };
    },
    applyRouteFilter() {
      this.filterFactureId = this.$route?.query?.facture_id
        ? String(this.$route.query.facture_id)
        : "";
      this.applyFilters();
    },
    onFactureFilterChange() {
      if (this.filterFactureId) {
        this.$router.replace({
          path: this.$route.path,
          query: { facture_id: this.filterFactureId },
        });
        return;
      }

      this.$router.replace({ path: this.$route.path, query: {} });
      this.applyFilters();
    },
    clearFilters() {
      this.searchQuery = "";
      this.filterFactureId = "";
      this.$router.replace({ path: this.$route.path, query: {} });
      this.applyFilters();
    },
    applyFilters() {
      const query = this.normalize(this.searchQuery).trim();

      this.filteredPayments = this.payments.filter((payment) => {
        const matchesFacture =
          !this.filterFactureId ||
          String(payment.facture_id) === String(this.filterFactureId);

        const matchesSearch =
          !query ||
          [
            payment.facture_reference,
            payment.client_name,
            payment.reference,
            payment.mode_paiement,
            payment.user_name,
            payment.notes,
            payment.montant,
          ]
            .filter((value) => value !== undefined && value !== null)
            .map((value) => this.normalize(value))
            .some((value) => value.includes(query));

        return matchesFacture && matchesSearch;
      });
    },
    resetForm() {
      this.editingPayment = null;
      this.form = {
        facture_id: this.filterFactureId || "",
        montant: null,
        mode_paiement: defaultPaymentMode,
        date_paiement: new Date().toISOString().slice(0, 10),
        reference: "",
        notes: "",
      };
    },
    async ensureModal() {
      const { Modal } = await import("bootstrap");
      if (!this.paymentModal && this.$refs.paymentModalEl) {
        this.paymentModal = new Modal(this.$refs.paymentModalEl, {
          backdrop: "static",
        });
      }
    },
    async openCreateModal() {
      this.resetForm();
      await this.ensureModal();
      this.paymentModal?.show();
    },
    async openEditModal(payment) {
      this.editingPayment = payment;
      this.form = {
        facture_id: String(payment.facture_id),
        montant: Number(payment.montant),
        mode_paiement:
          payment.mode_paiement && payment.mode_paiement !== "—"
            ? payment.mode_paiement
            : defaultPaymentMode,
        date_paiement: this.toDateInput(payment.date_paiement),
        reference: payment.reference || "",
        notes: payment.notes || "",
      };
      await this.ensureModal();
      this.paymentModal?.show();
    },
    hideModal() {
      this.paymentModal?.hide();
    },
    async loadFactures() {
      const rows = await getFacturesData();
      this.factures = (Array.isArray(rows) ? rows : [])
        .map((item) => this.mapFacture(item))
        .sort((a, b) => b.id - a.id);
    },
    async loadPayments() {
      const rows = await getFacturePaymentsData();
      this.payments = (Array.isArray(rows) ? rows : [])
        .map((item) => this.mapPayment(item))
        .sort((a, b) => new Date(b.date_paiement) - new Date(a.date_paiement));
      this.applyFilters();
    },
    async reloadData() {
      this.isLoading = true;
      try {
        await Promise.all([this.loadFactures(), this.loadPayments()]);
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Impossible de charger les paiements des factures.";
        toast.error(message);
      } finally {
        this.isLoading = false;
      }
    },
    async submitPayment() {
      if (!this.form.facture_id) {
        toast.error("Veuillez sélectionner une facture.");
        return;
      }

      if (!this.form.montant || Number(this.form.montant) <= 0) {
        toast.error("Le montant doit être supérieur à zéro.");
        return;
      }

      if (this.isAmountOverLimit) {
        toast.error("Le montant dépasse le reste autorisé.");
        return;
      }

      this.isSubmitting = true;

      const payload = {
        facture_id: Number(this.form.facture_id),
        montant: Number(this.form.montant),
        mode_paiement: this.emptyToNull(this.form.mode_paiement),
        date_paiement: this.emptyToNull(this.form.date_paiement),
        reference: this.emptyToNull(this.form.reference),
        notes: this.emptyToNull(this.form.notes),
      };

      try {
        if (this.editingPayment) {
          await updateFacturePayment(this.editingPayment.id, payload);
          toast.success("Paiement modifié avec succès.");
        } else {
          await createFacturePayment(payload);
          toast.success("Paiement ajouté avec succès.");
        }

        this.hideModal();
        await this.reloadData();
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Une erreur est survenue lors de l'enregistrement du paiement.";
        toast.error(message);
      } finally {
        this.isSubmitting = false;
      }
    },
    async confirmDelete(payment) {
      const result = await Swal.fire({
        title: "Supprimer ce paiement ?",
        text: "Le reste à payer de la facture sera recalculé.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        await deleteFacturePayment(payment.id);
        toast.success("Paiement supprimé avec succès.");
        await this.reloadData();
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Impossible de supprimer ce paiement.";
        toast.error(message);
      }
    },
  },
  async mounted() {
    await this.ensureModal();
    await this.reloadData();
    this.applyRouteFilter();
  },
  beforeUnmount() {
    this.paymentModal?.dispose();
    this.paymentModal = null;
  },
};
</script>

<style scoped>
.summary-chip {
  min-width: 150px;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  background: #f7f9fc;
}

.summary-label {
  display: block;
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 0.15rem;
}
</style>
